/**
 * Site-entity meta sidecar — feature associations and per-(user,site)
 * permissions that ride alongside the site but aren't unit/alarm data.
 *
 * Two maps keyed by site id: the site's feature_associations (drives feature
 * gates like the Weather section) and the meta.permissions block (drives the
 * dashboard's action affordances). Seeded by the retrieve fan-out. The feature
 * list also stays live when a site feature is toggled from the Settings
 * Features section (FeatureCard → create/editFeatureAssociation) so a gate
 * updates without a full settings re-fetch.
 *
 * Contains:
 * - siteMeta slice (setMeta, setFeatureAssociations) + reducer
 */
import { createSlice } from '@reduxjs/toolkit';

import { createFeatureAssociation, editFeatureAssociation } from '@sophocles/features/featureFlags/api';

/**
 * Upsert a single feature association into a site's list, matching on
 * feature_key. Mirrors a site-scoped FeatureAssociation write (Settings
 * Features section) so the gate selectors stay in sync without a re-fetch.
 *
 * The features/slice.js reducer runs first (registered earlier in rootReducer)
 * and may have coerced payload.feature from an object to a string key — read
 * feature_key first, then fall back to feature whether object or string.
 */
function applyFeatureAssociationPatch(state, key, payload) {
  const featureKey =
    payload.feature_key ?? (typeof payload.feature === 'string' ? payload.feature : payload.feature?.key);
  if (!featureKey) return;

  const existing = state.featureAssociationsBySiteId[key] ?? [];
  const idx = existing.findIndex(a => a.feature_key === featureKey);
  const patch = {
    is_enabled: payload.is_enabled,
    is_effectively_enabled: payload.is_effectively_enabled,
    is_disabled_from_parent: payload.is_disabled_from_parent ?? false,
  };

  state.featureAssociationsBySiteId[key] =
    idx === -1
      ? [...existing, { feature_key: featureKey, ...patch }]
      : existing.map((a, i) => (i === idx ? { ...a, ...patch } : a));
}

const slice = createSlice({
  name: 'siteMeta',
  initialState: { featureAssociationsBySiteId: {}, permissionsBySiteId: {} },
  reducers: {
    /** Seed both maps for a site from the composite retrieve fan-out. */
    setMeta(state, action) {
      const { siteId, featureAssociations, permissions } = action.payload;
      const key = String(siteId);
      state.featureAssociationsBySiteId[key] = featureAssociations ?? [];
      state.permissionsBySiteId[key] = permissions ?? null;
    },
    /** Replace a site's whole feature list (the site feature-association upsert). */
    setFeatureAssociations(state, action) {
      const { siteId, featureAssociations } = action.payload;
      state.featureAssociationsBySiteId[String(siteId)] = featureAssociations ?? [];
    },
  },
  extraReducers: builder => {
    // Mirror site-scoped feature toggles from the Settings Features section into
    // the site's feature list. Guard: entity_type must be 'site' and the site
    // must already be seeded (a featureless site still has an [] entry after
    // setMeta, so an absent key means "not loaded" → drop).
    const mirror = (state, action) => {
      const { entity_id, entity_type } = action.payload;
      if (entity_type !== 'site') return;
      const key = String(entity_id);
      if (state.featureAssociationsBySiteId[key] == null) return;
      applyFeatureAssociationPatch(state, key, action.payload);
    };
    builder.addCase(createFeatureAssociation.fulfilled, mirror).addCase(editFeatureAssociation.fulfilled, mirror);
  },
});

export const { setMeta, setFeatureAssociations } = slice.actions;
export default slice.reducer;
